// Attribution cookie rules (spec 2026-09-14-meta-ads-end-to-end-tracking-design §4.2).
// Pure: takes URL/referrer/time as inputs. Unit tested in tests/tracking/attributionCookie.test.ts.

export const ATTRIBUTION_COOKIE = 'w369_attr';
export const ATTRIBUTION_MAX_AGE_SEC = 90 * 24 * 60 * 60;
export const ATTRIBUTION_MAX_CHARS = 3072;

export const TOUCH_FIELDS = [
  'landing_path',
  'referrer_host',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'fbclid',
  'fbc',
  'gclid',
  'gbraid',
  'wbraid',
  'w369_src',
  'w369_plc',
] as const;

export type TouchField = (typeof TOUCH_FIELDS)[number];
export type Touch = { ts: number } & Record<TouchField, string>;
export type AttributionState = { v: 1; first: Touch | null; last: Touch | null; last_meta: Touch | null };

const CLICK_ID_FIELDS: ReadonlySet<TouchField> = new Set<TouchField>(['fbclid', 'fbc', 'gclid', 'gbraid', 'wbraid']);
// gbraid/wbraid are Google's click ids for iOS app and web traffic where gclid isn't
// allowed (ATT); the API uploads Google Ads conversions with whichever one a touch has.
const CLICK_ID_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid'];
// The Google click ids a touch's own gclid makes redundant: Google Ads accepts one
// click id per conversion, and gclid is the precise one, so it wins.
const BRAID_FIELDS = ['gbraid', 'wbraid'] as const;
// fbclid/gclid/gbraid/wbraid values these providers actually issue are URL-safe; anything else
// (control characters, multi-byte text, a stray '+' from a bad encode, etc.) means
// the id can't be trusted — and, unfiltered, can inflate the JSON-escaped cookie
// payload by many times its own length.
const CLEAN_CLICK_ID = /^[A-Za-z0-9_.-]+$/;
const cleanClickId = (value: string) => (value && CLEAN_CLICK_ID.test(value) ? value : '');
// The backend's own FB_COOKIE_PATTERN (Workspace369-API adAttribution.ts) — an fbc
// that doesn't match this exact shape is not a value buildFbc could have produced.
const FB_COOKIE_PATTERN = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_.-]+$/;

// Text is clipped to 200 chars; a truncated click id is corrupt, so over-long ones are dropped.
const clip = (field: TouchField, value: string) =>
  CLICK_ID_FIELDS.has(field) ? (value.length <= 500 ? value : '') : value.slice(0, 200);

export function buildFbc(fbclid: string, clickTimeMs: number): string {
  return fbclid && CLEAN_CLICK_ID.test(fbclid) ? `fb.1.${Math.floor(clickTimeMs)}.${fbclid}` : '';
}

// True when the URL carries any utm_-prefixed param with a value, or a Meta/Google
// click id (fbclid, gclid, gbraid, wbraid) that passes the clean-click-id rule — a dirty click id
// gets dropped by buildTouch anyway, so it shouldn't count as a campaign signal
// either (it would otherwise overwrite `last` with a touch that records nothing).
export function hasCampaignParams(url: URL): boolean {
  for (const [key, value] of url.searchParams) {
    if (!value) continue;
    if (key.startsWith('utm_')) return true;
    if (CLICK_ID_PARAMS.includes(key) && cleanClickId(value)) return true;
  }
  return false;
}

export function buildTouch(url: URL, referrer: string, nowMs: number): Touch {
  const get = (key: string) => url.searchParams.get(key) || '';
  let referrerHost = '';
  try {
    referrerHost = referrer ? new URL(referrer).hostname : '';
  } catch {
    referrerHost = '';
  }
  const fbclid = cleanClickId(get('fbclid'));
  const gclid = cleanClickId(get('gclid'));
  const raw: Record<TouchField, string> = {
    landing_path: url.pathname,
    referrer_host: referrerHost,
    utm_source: get('utm_source'),
    utm_medium: get('utm_medium'),
    utm_campaign: get('utm_campaign'),
    utm_term: get('utm_term'),
    utm_content: get('utm_content'),
    utm_id: get('utm_id'),
    fbclid,
    fbc: buildFbc(fbclid, nowMs),
    gclid,
    gbraid: gclid ? '' : cleanClickId(get('gbraid')),
    wbraid: gclid ? '' : cleanClickId(get('wbraid')),
    w369_src: get('w369_src'),
    w369_plc: get('w369_plc'),
  };
  const touch = { ts: nowMs } as Touch;
  for (const field of TOUCH_FIELDS) touch[field] = clip(field, raw[field]);
  return touch;
}

// Meta defines fbc's embedded timestamp as when the click id was first seen, not
// when it last reappeared. If this fbclid already exists on a stored touch with a
// non-empty fbc, carry that fbc forward instead of the freshly-built one so the
// timestamp doesn't drift on repeat visits/shares of the same link. The stored fbc
// must actually end in this fbclid — a decoded cookie is untrusted input, and a
// tampered/corrupt pairing must not get reused as if it were legitimate.
function withStableFbc(state: AttributionState, touch: Touch): Touch {
  if (!touch.fbclid) return touch;
  for (const existing of [state.last_meta, state.last, state.first]) {
    if (existing && existing.fbclid === touch.fbclid && existing.fbc && existing.fbc.endsWith(`.${touch.fbclid}`)) {
      return { ...touch, fbc: existing.fbc };
    }
  }
  return touch;
}

export function mergeAttribution(existing: AttributionState | null, touch: Touch, url: URL): AttributionState {
  const state = existing ?? { v: 1 as const, first: null, last: null, last_meta: null };
  const stableTouch = withStableFbc(state, touch);
  return {
    v: 1,
    first: state.first ?? stableTouch,
    last: hasCampaignParams(url) || !state.last ? stableTouch : state.last,
    last_meta: url.searchParams.get('utm_source') === 'meta' ? stableTouch : state.last_meta,
  };
}

export function encodeAttribution(state: AttributionState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Matches the backend's parseAdTouch: an array isn't a touch, and an object left
// with every field blank after sanitizing carries no signal, so both decode to null
// rather than a hollow Touch. A decoded cookie is untrusted input — it need not have
// come from encodeAttribution at all — so fbclid/gclid/gbraid/wbraid are re-run
// through the same clean-click-id rule buildTouch applies (and gclid still wins over
// the braids), and fbc must match the backend's own FB_COOKIE_PATTERN; any check
// failing zeroes that field, exactly as it would have been zeroed at write time.
function sanitizeTouch(value: unknown): Touch | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const touch = { ts: typeof raw.ts === 'number' && Number.isFinite(raw.ts) ? raw.ts : 0 } as Touch;
  let hasValue = false;
  for (const field of TOUCH_FIELDS) {
    let clipped = typeof raw[field] === 'string' ? clip(field, raw[field] as string) : '';
    if (field === 'fbclid' || field === 'gclid' || field === 'gbraid' || field === 'wbraid') clipped = cleanClickId(clipped);
    else if (field === 'fbc' && !FB_COOKIE_PATTERN.test(clipped)) clipped = '';
    touch[field] = clipped;
    if (clipped) hasValue = true;
  }
  if (touch.gclid) for (const field of BRAID_FIELDS) touch[field] = '';
  return hasValue ? touch : null;
}

export function decodeAttribution(raw: string | null | undefined): AttributionState | null {
  if (!raw) return null;
  try {
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    if (!parsed || parsed.v !== 1) return null;
    return {
      v: 1,
      first: sanitizeTouch(parsed.first),
      last: sanitizeTouch(parsed.last),
      last_meta: sanitizeTouch(parsed.last_meta),
    };
  } catch {
    return null;
  }
}

const truncateText = (touch: Touch | null, max: number): Touch | null => {
  if (!touch) return touch;
  const next = { ...touch };
  for (const field of TOUCH_FIELDS) if (!CLICK_ID_FIELDS.has(field)) next[field] = next[field].slice(0, max);
  return next;
};

const withoutClickIds = (touch: Touch | null): Touch | null =>
  touch ? { ...touch, fbclid: '', fbc: '', gclid: '', gbraid: '', wbraid: '' } : touch;

// last_meta is the Meta touch; Google's braids are the one part of it Meta never reads.
const withoutBraids = (touch: Touch | null): Touch | null =>
  touch ? { ...touch, gbraid: '', wbraid: '' } : touch;

// Reduces last_meta to its essentials: ts, click ids, utm_source and utm_content
// are kept as-is — the backend only keeps last_meta at all when utm_source ===
// 'meta', so blanking it would make this whole last-resort step self-defeating,
// and utm_content is the Meta ad id P2 reads, so it must survive too. Every other
// text field is blanked: by the time this step runs, step 5 has already truncated
// all text to 20 chars, so a longer clip here would never apply.
const blankText = (touch: Touch | null): Touch | null => {
  if (!touch) return touch;
  const next = { ...touch };
  for (const field of TOUCH_FIELDS) {
    if (CLICK_ID_FIELDS.has(field) || field === 'utm_source' || field === 'utm_content') continue;
    next[field] = '';
  }
  return next;
};

// Progressively sheds detail until the encoded cookie fits. last_meta's click ids
// are the most valuable signal and are shed last: first and last are truncated,
// then stripped of click ids, then dropped entirely, and only then is last_meta
// itself reduced to ts/click-ids/utm_source/utm_content (already <= 20 chars by
// this point), and last of all loses its gbraid/wbraid. That final shape — last_meta
// alone, with fbclid, fbc and gclid at their 500-char ceiling — encodes to well under
// ATTRIBUTION_MAX_CHARS, so this function always returns a string.
export function fitAttributionForCookie(state: AttributionState): string {
  const steps: Array<(s: AttributionState) => AttributionState> = [
    (s) => s,
    (s) => ({ ...s, first: truncateText(s.first, 60), last: truncateText(s.last, 60), last_meta: truncateText(s.last_meta, 60) }),
    (s) => ({ ...s, first: withoutClickIds(s.first) }),
    (s) => (s.last && s.last_meta && s.last.ts === s.last_meta.ts ? { ...s, last: withoutClickIds(s.last) } : s),
    (s) => ({ ...s, first: truncateText(s.first, 20), last: truncateText(s.last, 20), last_meta: truncateText(s.last_meta, 20) }),
    (s) => ({ ...s, last: withoutClickIds(s.last) }),
    (s) => ({ ...s, first: null, last: null }),
    (s) => ({ ...s, last_meta: blankText(s.last_meta) }),
    (s) => ({ ...s, last_meta: withoutBraids(s.last_meta) }),
  ];
  let current = state;
  let encoded = '';
  for (const step of steps) {
    current = step(current);
    encoded = encodeAttribution(current);
    if (encoded.length <= ATTRIBUTION_MAX_CHARS) return encoded;
  }
  return encoded;
}

// A pending touch parked in sessionStorage while ad consent is still unknown — the
// landing touch waits here until the visitor decides (spec §4.2, §4.6).
export type PendingTouch = { href: string; touch: Touch };
export type AdConsentValue = 'granted' | 'denied' | 'unknown';
export type LandingPlan = {
  persist: PendingTouch[]; // merge into the cookie in this order
  pending: 'keep' | 'write' | 'clear'; // what to do with the stored pending touch
  expireCookie: boolean; // expire w369_attr
};

// A stored pending touch is untrusted input (sessionStorage, not a signed cookie):
// its href must parse as an absolute URL, and its touch is round-tripped through
// encode/decode so it gets exactly the sanitizing a cookie value would.
export function parsePendingTouch(raw: string | null | undefined): PendingTouch | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const { href, touch } = parsed as { href?: unknown; touch?: unknown };
  if (typeof href !== 'string') return null;
  try {
    void new URL(href);
  } catch {
    return null;
  }
  const sanitized = decodeAttribution(
    encodeAttribution({ v: 1, first: touch as Touch, last: null, last_meta: null }),
  )?.first;
  if (!sanitized) return null;
  return { href, touch: sanitized };
}

// Decides what to do with the landing touch and any pending touch left over from
// an earlier undecided visit (spec §4.2, §4.6). Pure: no cookie/storage access —
// the plugin applies the returned plan.
export function planLanding(adConsent: AdConsentValue, pendingRaw: string | null, landing: PendingTouch): LandingPlan {
  const pending = parsePendingTouch(pendingRaw);

  if (adConsent === 'granted') {
    return { persist: pending ? [pending, landing] : [landing], pending: 'clear', expireCookie: false };
  }
  if (adConsent === 'denied') {
    return { persist: [], pending: 'clear', expireCookie: true };
  }

  // Still unknown: keep the landing touch until the visitor chooses. A later full
  // page load inside the site must not replace a paid landing touch with an
  // internal organic one, so only a *different* campaign landing may overwrite an
  // existing pending touch — a reload of the same campaign link leaves it alone.
  const landingUrl = new URL(landing.href);
  const isNewCampaignLanding =
    !pending || (hasCampaignParams(landingUrl) && landingUrl.search !== new URL(pending.href).search);
  return { persist: [], pending: isNewCampaignLanding ? 'write' : 'keep', expireCookie: false };
}

// The next w369_attr cookie value after merging `touch`, or null when nothing
// would change — so callers can skip the write.
export function nextAttributionCookie(existingRaw: string | null | undefined, touch: Touch, url: URL): string | null {
  const existing = decodeAttribution(existingRaw);
  const next = fitAttributionForCookie(mergeAttribution(existing, touch, url));
  return existing && encodeAttribution(existing) === next ? null : next;
}

const PATH_CLICK_ID_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid'] as const;

// Case/encoding-insensitive query key, matching the backend's own queryKey.
function pathQueryKey(pair: string): string {
  const raw = pair.split('=')[0];
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' ')).toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
}

// Removes ad click ids from a stored path's query string, e.g.
// "/pricing/?utm_source=meta&fbclid=x" -> "/pricing/?utm_source=meta". Ported from
// the backend's stripClickIds (Workspace369-API functions-lead-intake
// adAttribution.ts) so a lead's source_path never carries a click id past the
// browser without granted consent. Keeps every other param in order and the hash;
// drops a trailing "?" when no params are left.
export function stripClickIdsFromPath(path: string): string {
  const queryStart = path.indexOf('?');
  if (queryStart === -1) return path;
  const hashStart = path.indexOf('#', queryStart);
  const query = path.slice(queryStart + 1, hashStart === -1 ? undefined : hashStart);
  const hash = hashStart === -1 ? '' : path.slice(hashStart);
  const kept = query
    .split('&')
    .filter((pair) => pair !== '' && !(PATH_CLICK_ID_PARAMS as readonly string[]).includes(pathQueryKey(pair)));
  return path.slice(0, queryStart) + (kept.length ? `?${kept.join('&')}` : '') + hash;
}
